import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Backdrop, CircularProgress, Slide, Snackbar, useMediaQuery } from '@mui/material';
import { motion } from 'framer-motion';
import './JobDescriptionStyle.css';
import copyIcon from '../assets/copy.png';
import Header from './Header';
import { useNavigate } from 'react-router-dom';
import { endpoint } from '../../resource/Constant';
import { useTheme } from "@mui/material/styles";

const JobDescriptionGenerator = () => {
  const [step, setStep] = useState(1);
  const [jobTitle, setJobTitle] = useState('');
  const [experience, setExperience] = useState('');
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [companyLocation, setCompanyLocation] = useState('');
  const [suggestedSkills, setSuggestedSkills] = useState([]);
  const [loader, setLoader] = useState(false);
  const [customSkill, setCustomSkill] = useState([]);
  const [jobDescription, setJobDescription] = useState(null);
  const ref = useRef(null);
  const navigate = useNavigate();
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const stepVariants = {
    initial: { opacity: 0, x: 50 }, // Start off-screen
    animate: { opacity: 1, x: 0 }, // Slide in
    exit: { opacity: 0, x: -50 } // Slide out
  };
  const theme = useTheme(); // Get the theme object
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm")); // Below 600px

  const copyToClipboard = () => {
    if (ref.current) {
      const text = ref.current.innerText;
      navigator.clipboard
        .writeText(text)
        .then(() =>
          setSnackbar({
            open: true,
            message: 'Copied to clipboard',
            severity: 'success'
          })
        )
        .catch((err) =>
          setSnackbar({
            open: true,
            message: err,
            severity: 'error'
          })
        );
    }
  };

  const nextStep = () => {
    // if (step)
    if (step == 4) {
    }
    if (step < 5) setStep(step + 1);
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleRemoveSkill = (skill) => {
    setSelectedSkills(selectedSkills.filter((s) => s !== skill));
  };

  const handleSkillClick = (skill) => {
    setSelectedSkills((prev) => (prev.includes(skill) ? prev.filter((s) => s !== skill) : [...prev, skill]));
  };

  const generateSkills = () => {
    try {
      setLoader(true);
      axios
        .post(
          endpoint + `/api/generate-skills?job_title=${jobTitle}&experience=${experience}`,
          {},
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('jobdesctoken')}`,
              'Content-Type': 'application/json'
            }
          }
        )
        .then((res) => {
          setLoader(false);
          setSuggestedSkills(res.data.skills);
          nextStep();
        })
        .catch((err) => {
          setSnackbar({
            open: true,
            message: err.response.data.detail,
            severity: 'error'
          });
          if (err.response.data.detail === 'Token has expired') {
            localStorage.removeItem('jobdesctoken');
            setTimeout(() => {
              navigate('/jobdescription/login');
            }, 1000);
          }
        });
    } catch (error) {
      setLoader(false);
    }
  };

  const generateJobDescription = () => {
    try {
      setLoader(true);
      axios
        .post(
          endpoint + `/api/generate-job-description`,
          {
            job_title: jobTitle,
            experience: experience,
            location: companyLocation,
            company: companyName,
            additionalInfo: additionalInfo,
            requirements: selectedSkills
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('jobdesctoken')}`,
              'Content-Type': 'application/json'
            }
          }
        )
        .then((res) => {
          setJobDescription(res.data);
          nextStep();
        })
        .catch((err) => {
          setSnackbar({
            open: true,
            message: err.response.data.detail,
            severity: 'error'
          });
        })
        .finally(() => {
          setLoader(false);
        });
    } catch (error) {
      setLoader(false);
    }
  };

  return (
    <>
      <div style={{ padding: '20px' }}>
        <Header />
      </div>
      <div className="job-description">
        <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-6">
          <div className="w-full max-w-2xl bg-white p-8 rounded-lg shadow-lg">
            <div className="text-center">
              <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">Job Description Generator</span>
              <h1 className="text-3xl font-bold mt-2">Create the Perfect Job Description</h1>
              <p className="text-gray-500 mt-2">Craft professional job descriptions in minutes, not hours.</p>
            </div>

            {/* Progress Stepper */}
            <div className="flex justify-between items-center my-6">
              {[1, 2, 3, 4, 5].map((num) => (
                <div key={num} className="flex flex-col items-center">
                  <div
                    className={`w-8 h-8 flex items-center justify-center rounded-full text-white font-bold ${
                      num <= step ? 'bg-blue-500' : 'bg-gray-300'
                    }`}
                  >
                    {num}
                  </div>
                  <p style={{fontSize : isSmallScreen ? '8px' : '14px' }} className={`text-sm ${num === step ? 'font-semibold' : 'text-gray-400'}`}>
                    {['Job Title', 'Skills', 'Additional Info', 'Company Info', 'Result'][num - 1]}
                  </p>
                </div>
              ))}
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
              <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${(step / 5) * 100}%` }}></div>
            </div>

            {/* Step 1: Job Title Form */}
            {step === 1 && (
              <motion.div
                key="step1"
                variants={stepVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.4 }}
              >
                <h2 className="text-xl font-semibold mb-2">What job are you hiring for?</h2>
                <p className="text-gray-500 mb-4">Enter the job title and required experience to get started.</p>

                <label className="block text-gray-700 font-medium mb-1">Job Title</label>
                <input
                  type="text"
                  style={{ color: 'black' }}
                  placeholder="e.g. Software Engineer, Product Manager, UX Designer"
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md mb-4"
                />

                <label className="block text-gray-700 font-medium mb-1">Experience Level</label>
                <select
                  style={{ color: 'black' }}
                  value={experience}
                  onChange={(e) => setExperience(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                >
                  <option value="">Select required experience</option>
                  <option style={{ color: 'black' }} value="Entry Level">
                    Entry Level (0-2 years)
                  </option>
                  <option style={{ color: 'black' }} value="Mid Level">
                    Mid Level (2-5 years)
                  </option>
                  <option style={{ color: 'black' }} value="Senior Level">
                    Senior Level (5+ years)
                  </option>
                </select>

                <button
                  className={`mt-6 w-full py-2 rounded-md transition duration-300
                ${
                  jobTitle && experience
                    ? 'bg-blue-600 hover:bg-blue-700 text-white cursor-pointer'
                    : 'bg-gray-400 text-gray-700 cursor-not-allowed'
                }`}
                  onClick={generateSkills}
                  disabled={!jobTitle || !experience}
                >
                  Next →
                </button>
              </motion.div>
            )}

            {/* Step 2: Skills Selection */}
            {step === 2 && (
              <motion.div
                key="step2"
                variants={stepVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.4 }}
              >
                <h2 className="text-xl font-semibold mb-2">Select Required Skills</h2>
                <p className="text-gray-500 mb-4">
                  We've suggested skills based on the job title "{jobTitle}". Select the skills required for this position.
                </p>

                {/* Suggested Skills List */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {suggestedSkills.map((skill) => (
                    <button
                      key={skill}
                      onClick={() => handleSkillClick(skill)}
                      className={`px-4 py-2 rounded-full border ${
                        selectedSkills.includes(skill) ? 'bg-blue-500 text-white' : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      {skill}
                    </button>
                  ))}
                </div>

                {/* Input Field for Custom Skills */}
                <div className="mb-4 flex items-center gap-2">
                  <input
                    type="text"
                    value={customSkill}
                    required
                    style={{ color: 'black' }}
                    onChange={(e) => setCustomSkill(e.target.value)}
                    className="border border-gray-300 rounded-md px-3 py-2 w-full"
                    placeholder="Type a skill and press Enter"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && customSkill.trim() !== '') {
                        handleSkillClick(customSkill.trim());
                        setCustomSkill(''); // Clear input field
                      }
                    }}
                  />
                  <button
                    disabled={customSkill.length <= 1}
                    onClick={() => {
                      if (customSkill.trim() !== '') {
                        handleSkillClick(customSkill.trim());
                        setCustomSkill('');
                      }
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md"
                  >
                    Add
                  </button>
                </div>

                {/* Selected Skills with Remove Option */}
                <h3 className="text-lg font-semibold">Selected Skills ({selectedSkills.length})</h3>
                {selectedSkills.length > 0 ? (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {selectedSkills.map((skill) => (
                      <div key={skill} className="flex items-center bg-blue-100 text-blue-700 rounded-full text-sm px-3 py-1">
                        {skill}
                        <button onClick={() => handleRemoveSkill(skill)} className="ml-2 text-red-500 hover:text-red-700">
                          ❌
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm mt-2">No skills selected yet. Click on the skills above or type your own.</p>
                )}

                <div className="flex justify-between mt-6">
                  <button className="px-4 py-2  bg-blue-600 rounded" onClick={prevStep} style={{ color: 'white' }}>
                    ← Back
                  </button>
                  <button onClick={nextStep} className="px-4 py-2 rounded bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700">
                    Next →
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 3: Additional Information */}
            {step === 3 && (
              <motion.div
                key="step3"
                variants={stepVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.4 }}
              >
                <h2 className="text-xl font-semibold mb-2">Additional Information</h2>
                <p className="text-gray-500 mb-4">
                  Add any additional detail that would help potential candidates understand the role better.
                </p>
                <label className="block text-gray-700 font-medium mb-1">Additional Requirements or Perks</label>
                <textarea
                  style={{ color: 'black' }}
                  placeholder="List any requirements, benefits, or important info about the role. E.g. remote work, flexible hours, healthcare benefits."
                  value={additionalInfo}
                  onChange={(e) => setAdditionalInfo(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md h-24"
                />
                <p className="text-gray-500 text-sm mt-4">
                  Additional information might include working hours, benefits package, growth opportunities, or company culture.
                </p>
                <div className="flex justify-between mt-6">
                  <button className="px-4 py-2 bg-blue-600 rounded" onClick={prevStep} style={{ color: 'white' }}>
                    ← Back
                  </button>
                  <button onClick={nextStep} className="px-4 py-2 rounded bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700">
                    Next →
                  </button>
                </div>
              </motion.div>
            )}

            {step === 4 && (
              <motion.div
                key="step4"
                variants={stepVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.4 }}
              >
                <h2 className="text-xl font-semibold">Company Information</h2>
                <p className="text-gray-600 mt-2">
                  Tell potential candidates about your company. This information will help create a more personalized job description.
                </p>

                <div className="mt-4">
                  <label className="block text-gray-700">Company Name</label>
                  <input
                    value={companyName}
                    style={{ color: 'black' }}
                    onChange={(e) => setCompanyName(e.target.value)}
                    type="text"
                    className="w-full border rounded-lg p-3 mt-2"
                    placeholder="e.g. Acme Corporation"
                  />
                </div>

                <div className="mt-4">
                  <label className="block text-gray-700">Location</label>
                  <input
                    style={{ color: 'black' }}
                    value={companyLocation}
                    onChange={(e) => setCompanyLocation(e.target.value)}
                    type="text"
                    className="w-full border rounded-lg p-3 mt-2"
                    placeholder="e.g. San Francisco, CA or Remote"
                  />
                </div>

                <div className="flex justify-between mt-6">
                  <button onClick={prevStep} className="px-4 py-2  bg-blue-600 rounded" style={{ color: 'white' }}>
                    ← Back
                  </button>
                  <button
                    onClick={generateJobDescription}
                    className="px-4 py-2 rounded bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700"
                  >
                    Generate
                  </button>
                </div>
              </motion.div>
            )}

            <div style={{ textAlign: 'left', color: 'black' }}>
              {step === 5 && jobDescription && (
                <motion.div
                  key="step5"
                  variants={stepVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  transition={{ duration: 0.4 }}
                  className="mt-6"
                >
                  <h2 className="text-xl font-semibold mb-2">Generated Job Description</h2>
                  <p className="text-gray-500 mb-4">Here's your completed job description. You can copy it or edit it further as needed.</p>
                  <div ref={ref} className="border rounded-lg p-4 bg-gray-100">
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <h3 className="text-lg font-bold">
                        {jobDescription.job_title || '[Job Title]'}
                        {companyName && ' at '}
                        {companyName || ''}
                      </h3>
                      <button
                        // className="bg-blue-200 text-white py-1 px-2 text-sm rounded hover:bg-blue-700"
                        onClick={copyToClipboard}
                        data-ignore="true" // Mark this button to be ignored
                      >
                        <img style={{ height: '25px' }} src={copyIcon} alt="Copy" />
                      </button>
                    </div>
                    <p className="text-blue-500 font-semibold">{jobDescription.experience || '[Experience Level]'}</p>
                    {companyName && <h4 className="font-bold mt-2">About {companyName || '[Company Name]'}</h4>}
                    {companyName && (
                      <p style={{ color: 'black' }}>{jobDescription.company_culture || 'Company culture description not available.'}</p>
                    )}

                    <h4 className="font-bold mt-2">The Role</h4>
                    <p style={{ color: 'black' }}>{jobDescription.job_description || 'Job description not available.'}</p>

                    <h4 className="font-bold mt-2">Responsibilities</h4>
                    <ul className="list-disc list-inside">
                      {jobDescription.responsibilities && jobDescription.responsibilities.length > 0 ? (
                        jobDescription.responsibilities.map((responsibility, index) => <li key={index}>{responsibility}</li>)
                      ) : (
                        <li>No responsibilities listed.</li>
                      )}
                    </ul>

                    <h4 className="font-bold mt-2">Skills Required</h4>
                    <ul className="list-disc list-inside">
                      {jobDescription.skills && jobDescription.skills.length > 0 ? (
                        jobDescription.skills.map((skill, index) => <li key={index}>{skill}</li>)
                      ) : (
                        <li>No skills listed.</li>
                      )}
                    </ul>

                    <h4 className="font-bold mt-2">Qualifications</h4>
                    <ul className="list-disc list-inside">
                      {jobDescription.qualifications && jobDescription.qualifications.length > 0 ? (
                        jobDescription.qualifications.map((qualification, index) => <li key={index}>{qualification}</li>)
                      ) : (
                        <li>No qualifications listed.</li>
                      )}
                    </ul>

                    <h4 className="font-bold mt-2">Career Growth Opportunities</h4>
                    <p style={{ color: 'black' }}>{jobDescription.career_growth_opportunities || 'Growth opportunities not specified.'}</p>
                    <p style={{ color: 'black' }}>{jobDescription.additional_info || 'additionalInfo not specified.'}</p>
                  </div>
                </motion.div>
              )}
            </div>
          </div>

          <Backdrop
            open={loader}
            sx={{
              zIndex: 1300,
              backgroundColor: 'rgba(0, 0, 0, 0.4)', // Darker for better visibility
              backdropFilter: 'blur(0px)' // Adds a smooth blur effect
            }}
          >
            {/* Loader Animation */}
            <div className="flex space-x-2">
              {[...Array(5)].map((_, i) => (
                <motion.div
                  key={i}
                  className="w-3 h-3 bg-blue-500 rounded-full"
                  animate={{ y: [-5, 5, -5] }}
                  transition={{
                    repeat: Infinity,
                    duration: 1.2,
                    delay: i * 0.2,
                    ease: 'easeInOut'
                  }}
                />
              ))}
            </div>
          </Backdrop>

          <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert
              onClose={() => setSnackbar({ ...snackbar, open: false })}
              severity={snackbar.severity}
              sx={{
                width: '100%',
                backgroundColor: '#0D47A1',
                color: '#fff',
                fontWeight: 'bold'
              }}
            >
              <div className="flex items-center gap-2">
                {snackbar.message}
                <span className="animate-bounce">.</span>
                <span className="animate-bounce delay-200">.</span>
                <span className="animate-bounce delay-400">.</span>
              </div>
            </Alert>
          </Snackbar>
        </div>
      </div>
    </>
  );
};

export default JobDescriptionGenerator;
